import { MapPin, ArrowRight } from 'lucide-react';

const QUARTERS_PT = ['Y1Q1', 'Y1Q2', 'Y1Q3', 'Y1Q4'];
const QUARTERS_ES = ['Y2Q1', 'Y2Q2', 'Y2Q3', 'Y2Q4'];
const LABELS = {
  Y1Q1: 'T1 · Ano 1',
  Y1Q2: 'T2 · Ano 1',
  Y1Q3: 'T3 · Ano 1',
  Y1Q4: 'T4 · Ano 1',
  Y2Q1: 'T1 · Ano 2',
  Y2Q2: 'T2 · Ano 2',
  Y2Q3: 'T3 · Ano 2',
  Y2Q4: 'T4 · Ano 2',
};

const uid = quarter => `${quarter.toLowerCase()}-${Date.now().toString(36).slice(-4)}${Math.random().toString(36).slice(2, 4)}`;

const groupByQuarter = milestones => {
  const groups = {};
  QUARTERS_PT.concat(QUARTERS_ES).forEach(q => { groups[q] = []; });
  (milestones || []).forEach(m => { if (groups[m.quarter]) groups[m.quarter].push(m); });
  return groups;
};

export const IberianTimelineEditor = ({ plan, editIberianTimeline }) => {
  if (!plan?.iberian_timeline) return null;
  const timeline = plan.iberian_timeline;
  const grouped = groupByQuarter(timeline.milestones);
  const setMilestones = list => editIberianTimeline('milestones', list);
  const editMilestone = (id, key, value) => setMilestones(timeline.milestones.map(m => (m.id === id ? { ...m, [key]: value } : m)));
  const addMilestone = quarter => {
    if (timeline.milestones.length >= 24) return;
    const market = quarter.startsWith('Y1') ? 'PT' : 'ES';
    setMilestones([...timeline.milestones, { id: uid(quarter), quarter, market, title: 'Novo marco', detail: '' }]);
  };
  const removeMilestone = id => setMilestones(timeline.milestones.filter(m => m.id !== id));

  const renderColumn = quarter => {
    const items = grouped[quarter];
    const market = quarter.startsWith('Y1') ? 'PT' : 'ES';
    return (
      <div key={quarter} className={`iberian-timeline-column ${market === 'PT' ? 'pt' : 'es'}`} data-testid={`timeline-column-${quarter}`}>
        <header>
          <span className="iberian-quarter-label">{LABELS[quarter]}</span>
          <span className="iberian-market-flag" aria-hidden="true">{market === 'PT' ? '🇵🇹' : '🇪🇸'}</span>
        </header>
        {items.length === 0 && <p className="iberian-empty" data-testid={`timeline-empty-${quarter}`}>Sem marcos.</p>}
        {items.map(m => (
          <article key={m.id} className="iberian-milestone" data-testid={`timeline-milestone-${m.id}`}>
            <label>
              <span>Marco</span>
              <input type="text" maxLength={80} value={m.title} data-testid={`timeline-title-${m.id}`} onChange={e => editMilestone(m.id, 'title', e.target.value)} />
            </label>
            <label>
              <span>Detalhe</span>
              <textarea rows={2} maxLength={200} value={m.detail || ''} data-testid={`timeline-detail-${m.id}`} onChange={e => editMilestone(m.id, 'detail', e.target.value)} />
            </label>
            <button type="button" data-testid={`timeline-remove-${m.id}`} onClick={() => removeMilestone(m.id)} aria-label="Remover marco">Remover</button>
          </article>
        ))}
        <button type="button" className="iberian-add-btn" data-testid={`timeline-add-${quarter}`} onClick={() => addMilestone(quarter)} disabled={timeline.milestones.length >= 24}>+ Marco {LABELS[quarter]}</button>
      </div>
    );
  };

  return (
    <section className="plan-group iberian-timeline-group" data-testid="iberian-timeline-editor">
      <div className="plan-group-heading">
        <h3><MapPin size={17} aria-hidden="true" /> Cronograma Ibérico · 12 meses PT → Ano 2 ES</h3>
        <p>Mini-cronograma editável, mostrado no anexo do plano. Sem impacto nos cálculos — comunica o calendário narrativo da expansão.</p>
      </div>
      <label className="iberian-timeline-toggle" data-testid="timeline-enabled-toggle">
        <input type="checkbox" checked={!!timeline.enabled} onChange={e => editIberianTimeline('enabled', e.target.checked)} />
        <span>Mostrar cronograma no anexo do plano</span>
      </label>
      <div className="iberian-timeline-row">
        <h4>🇵🇹 Ano 1 · Portugal</h4>
        <div className="iberian-timeline-columns">{QUARTERS_PT.map(renderColumn)}</div>
      </div>
      <div className="iberian-timeline-row">
        <h4>🇪🇸 Ano 2 · Espanha</h4>
        <div className="iberian-timeline-columns">{QUARTERS_ES.map(renderColumn)}</div>
      </div>
    </section>
  );
};

export const IberianTimelineVisual = ({ timeline, prefix = 'timeline' }) => {
  if (!timeline || !timeline.enabled) return null;
  const grouped = groupByQuarter(timeline.milestones);
  return (
    <div className="iberian-timeline-visual" data-testid={`${prefix}-visual`}>
      <div className="iberian-timeline-track">
        {QUARTERS_PT.concat(QUARTERS_ES).map((q, i) => (
          <div key={q} className={`iberian-track-cell ${q.startsWith('Y1') ? 'pt' : 'es'}`}>
            <div className="iberian-track-header">
              <span>{LABELS[q]}</span>
              <span aria-hidden="true">{q.startsWith('Y1') ? '🇵🇹' : '🇪🇸'}</span>
            </div>
            <ul>
              {grouped[q].map(m => (
                <li key={m.id}>
                  <strong>{m.title}</strong>
                  {m.detail && <span>{m.detail}</span>}
                </li>
              ))}
              {grouped[q].length === 0 && <li className="empty">—</li>}
            </ul>
            {i === QUARTERS_PT.length - 1 && (
              <div className="iberian-track-transition" aria-hidden="true">
                <ArrowRight size={16} />
                <span>Expansão</span>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
